import React, { useMemo } from 'react';
import moment from 'moment';
import DeafaultGame from '@/pages/home/assets/img/common/game-default.svg';
import TinyEditor from '@sg/components/TinyEditor';
import Loading from '@intelligence/components/Loading';
import NoData from '@/pages/intelligence/components/NoData';
import { renderRichText } from '@sg/utils/commonPageMethod';
import TextBubbles from '@/pages/home/components/TextBubbles';
import classnames from 'classnames';
import { useIntl } from 'umi';
import type {
  IHighlightsProps,
  IHighlightProject,
  ISelectedEventInfo,
} from '../../type';
import './index.scss';

const emptyGroups = {
  grCheckInMeeting: [] as IHighlightProject[],
  toBeLaunched: [] as IHighlightProject[],
  riskOfDelay: [] as IHighlightProject[],
  majorUpdates: [] as IHighlightProject[],
};

const Highlights: React.FC<IHighlightsProps> = (props) => {
  const {
    segment,
    isEditing = false,
    notesContent = '',
    onNotesChange,
    selectedEvent = {} as ISelectedEventInfo,
    selectedMonthStr,
    data,
    loading,
    allProjects,
  } = props;

  const { formatMessage } = useIntl();

  const isNodata = useMemo(() => {
    return !allProjects?.length;
  }, [allProjects]);

  const isInDev = useMemo(() => {
    return segment === 'in_dev';
  }, [segment]);

  const currentMonthKey = data?.currentMonth?.date || selectedMonthStr;
  const nextMonthKey = data?.nextMonth?.date
    || moment(selectedMonthStr, 'YYYY-MM').add(1, 'month').format('YYYY-MM');

  const currentGroups = data?.currentMonth || emptyGroups;
  const nextGroups = data?.nextMonth || emptyGroups;

  const renderProjectItem = (proj: IHighlightProject) => {
    const cover = proj.cover || DeafaultGame;
    return (
      <div
        className="dbs-pipeline-highlights-project-item"
        key={String(proj.id)}
      >
        <img
          src={cover}
          alt={proj.project}
          onError={(e: any) => {
            e.target.src = DeafaultGame;
          }}
        />
        <TextBubbles text={proj.gameTitleAbbr || proj.project} />
      </div>
    );
  };

  const renderProjectList = (list: IHighlightProject[]) => {
    if (!list || list.length === 0) {
      return null;
    }
    return (
      <div className="dbs-pipeline-highlights-project-list">
        {list.map((p) => {
          return renderProjectItem(p);
        })}
      </div>
    );
  };

  return (
    <div className="dbs-pipeline-highlights">
      <h3 className="dbs-pipeline-highlights-title">Highlights</h3>
      {loading && <Loading classNameValue="dbs-loading" style={{ height: '629px' }} />}
      {!loading && (
        <>
          {/* Current Month */}
          {isInDev && (
            <div
              className={classnames('dbs-pipeline-highlights-section', {
                'dbs-studio-portfolio-overview-pipeline-calendar-indev': isInDev,
                'dbs-studio-portfolio-overview-pipeline-calendar-live': !isInDev,
              })}
            >
              <div className="dbs-pipeline-highlights-section-header">
                <h4>Current Month</h4>
                <span>{!isNodata ? currentMonthKey : ''}</span>
              </div>
              <div className="dbs-pipeline-highlights-section-content">
                <div className="dbs-pipeline-highlights-group">
                  <div className="dbs-pipeline-highlights-group-label">
                    GR/Check-in Meeting
                  </div>
                  {renderProjectList(currentGroups.grCheckInMeeting) || (
                    <span className="dbs-pipeline-highlights-group-label-text-empty">{'-'}</span>
                  )}
                </div>
                <div className="dbs-pipeline-highlights-group">
                  <div className="dbs-pipeline-highlights-group-label">
                    To be Launched
                  </div>
                  {renderProjectList(currentGroups?.toBeLaunched) || (
                    <span className="dbs-pipeline-highlights-group-label-text-empty">{'-'}</span>
                  )}
                </div>
                <div className="dbs-pipeline-highlights-group dbs-pipeline-highlights-group--risk">
                  <div className="dbs-pipeline-highlights-group-label">
                    <span className="dbs-pipeline-highlights-group-label-text">Risk of Delay</span>
                  </div>
                  {renderProjectList(currentGroups?.riskOfDelay) || '-'}
                </div>
              </div>
            </div>
          )}

          {!isInDev && (
            <div
              className={classnames('dbs-pipeline-highlights-section', {
                'dbs-studio-portfolio-overview-pipeline-calendar-indev': isInDev,
                'dbs-studio-portfolio-overview-pipeline-calendar-live': !isInDev,
              })}
            >
              <div className="dbs-pipeline-highlights-section-header">
                <h4>Current Month</h4>
                <span>{!isNodata ? currentMonthKey : ''}</span>
              </div>
              <div className="dbs-pipeline-highlights-section-content">
                <div className="dbs-pipeline-highlights-group">
                  <div className="dbs-pipeline-highlights-group-label">
                    Major Updates
                  </div>
                  {renderProjectList(currentGroups.majorUpdates) || (
                    <span className="dbs-pipeline-highlights-group-label-text-empty">{'-'}</span>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Next Month */}
          {isInDev && (
            <div
              className={classnames('dbs-pipeline-highlights-section', {
                'dbs-studio-portfolio-overview-pipeline-calendar-indev-next': isInDev,
              })}
            >
              <div className="dbs-pipeline-highlights-section-header">
                <h4>Next Month</h4>
                <span>{!isNodata ? nextMonthKey : ''}</span>
              </div>
              <div className="dbs-pipeline-highlights-section-content">
                <div className="dbs-pipeline-highlights-group">
                  <div className="dbs-pipeline-highlights-group-label">
                    GR/Check-in Meeting
                  </div>
                  {renderProjectList(nextGroups.grCheckInMeeting) || (
                    <span className="dbs-pipeline-highlights-group-label-text-empty">{'-'}</span>
                  )}
                </div>
                <div className="dbs-pipeline-highlights-group">
                  <div className="dbs-pipeline-highlights-group-label">
                    To be Launched
                  </div>
                  {renderProjectList(nextGroups.toBeLaunched) || (
                    <span className="dbs-pipeline-highlights-group-label-text-empty">{'-'}</span>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Selected Event Notes */}
          {(
            <div
              className={classnames('dbs-pipeline-highlights-notes', {
                'dbs-pipeline-highlights-notes-indev': isInDev,
                'dbs-pipeline-highlights-notes-live': !isInDev,
              })}
            >
              <div className="dbs-pipeline-highlights-notes-header">
                <h4>Notes</h4>
                <span>{selectedEvent?.date}</span>
              </div>
              {isEditing && (
                <div className="dbs-pipeline-highlights-notes-editor">
                  <TinyEditor
                    key={[
                      selectedEvent?.projectName ?? '',
                      selectedEvent?.eventName ?? '',
                      selectedEvent?.date ?? '',
                    ].join('_')}
                    value={notesContent}
                    getEditorContent={(content: string) => {
                      if (onNotesChange) {
                        onNotesChange(content);
                      }
                    }}
                    isShowToolbar={true}
                    minHeight={isInDev ? 200 : 408}
                    maxContentHeight={isInDev ? 200 : 408}
                    isMaxHeightScroll={true}
                    placeholder={formatMessage({
                      id: 'dbs_please_enter',
                    })}
                  />
                </div>
              )}
              {!isEditing && notesContent && (
                renderRichText(notesContent, 'dbs-pipeline-highlights-notes-content')
              )}
              {!isEditing && !notesContent && (
                <NoData text={'No Data'} />
              )}
            </div>
          )}
        </>)
      }
    </div>
  );
};

export default Highlights;
